// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — onglet « Rapports » d'une organisation (module reporting, lecture seule) :
//   • choix de l'exercice (exercices de l'organisation) et du dispositif (filtre des états prêts, aides, campagnes) ;
//   • les sept états (gouvernance, prêts, aides, campagnes, créances, trésorerie de démonstration, retenues simulées)
//     avec leurs mentions et leurs contrôles (équations juste / faux), exports Excel / PDF / HTML ;
//   • comparaison N / N-1 ;
//   • rapport annuel : aperçu provisoire, arrêt (ds_rapport_arreter ; 409 si déjà arrêté), documents arrêtés et leurs
//     versions (réédition motivée = nouvelle version ; versions remplacées consultables, empreintes contrôlées) ;
//   • état de campagne : agrégé, arrêt, détail nominatif paginé (ds_export_nominatif ; 403 expliqué), exports.
// Routes : backend/src/dispositifs/reporting/README.md § 2. Aucun chiffre n'est recalculé ici.
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import { FileLock2, FileText, History, Lock, ShieldCheck, Users } from 'lucide-react';
import { Badge, Bouton, BandeauInfo, Squelette } from '../../../design/composants';
import { useDs, toArray, libelle } from '../api';
import { Champ, Etat, Fenetre, PaginationDs } from '../commun';
import { francs, dateHeureLongue } from '../apiSocial';
import { useLectureSociale, EtatsLecture, FenetreActe, ChampMotif, motifValide, TitreSection } from '../communSocial';
import { useCampagnes } from '../apiCampagnes';
import { useRapports, ETATS, LIBELLES_ETATS, ETATS_PAR_DISPOSITIF, TYPES_DOCUMENT, STATUTS_DOCUMENT } from './apiRapports';
import { blocEtat, blocEtatCampagne, typeIndicateur, frDate } from './presentationRapports';
import { Mentions, BlocRapport, Controles, BoutonsExport, cellule } from './DocumentRapport';

const Pastilles = ({ options, valeur, onChange, libelleGroupe }) => (
  <div className="rh-vues rh-vues-ligne" role="group" aria-label={libelleGroupe}>
    {options.map(([c, l]) => <button key={c} type="button" className="rh-vue" aria-pressed={valeur === c} onClick={() => onChange(c)}>{l}</button>)}
  </div>
);

const Periode = ({ ex }) => (ex ? (
  <p style={{ margin: '0 0 10px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
    Exercice {ex.annee} : du {frDate(ex.debut)} au {frDate(ex.fin)} — situation au {frDate(ex.au)}{ex.en_cours ? ' (exercice en cours)' : ''}.
  </p>
) : null);

/** Tableaux d'un état ; avec un filtre de dispositif sans ligne, un message plutôt que des tableaux vides. */
function CorpsEtat({ code, e, dispositif }) {
  const b = blocEtat(code, e, dispositif);
  if (dispositif && b && !b.tableaux.some((t) => t.lignes.length)) return <p style={{ margin: '0 0 12px', fontSize: 14 }}>Aucune donnée pour ce dispositif dans cet état sur l'exercice.</p>;
  return <BlocRapport bloc={b ? { ...b, titre: null } : null} />;
}

// ─── États ─────────────────────────────────────────────────────────────────────────────────────
function VueEtats({ organisation, exercice, dispositif, libelleDispositif }) {
  const api = useRapports();
  const [code, setCode] = useState('prets');
  const l = useLectureSociale(() => api.etat(organisation.id, code, exercice), [api, organisation.id, code, exercice]);
  const e = l.donnees;
  const filtre = dispositif && ETATS_PAR_DISPOSITIF.includes(code);
  return (
    <>
      <Pastilles options={ETATS} valeur={code} onChange={setCode} libelleGroupe="États" />
      <section className="rh-carte">
        <EtatsLecture lecture={l} Icone={FileText} estVide={false}>
          {e && (
            <>
              <TitreSection>{LIBELLES_ETATS[code]} — exercice {e.exercice?.annee}</TitreSection>
              <Periode ex={e.exercice} />
              <Mentions mentions={e.mentions} />
              {dispositif && !filtre && <BandeauInfo ton="info">Cet état porte sur l'ensemble de l'organisation : le filtre de dispositif ne s'y applique pas.</BandeauInfo>}
              {filtre && <BandeauInfo ton="info">Lignes du dispositif « {libelleDispositif} » seulement ; la ligne Total de l'ensemble est retirée et les contrôles portent sur l'ensemble de l'organisation.</BandeauInfo>}
              <BoutonsExport exporter={(f) => api.exporterEtat(organisation.id, code, exercice, f)} libelle="Exporter l'état" />
              <CorpsEtat code={code} e={e} dispositif={filtre ? dispositif : ''} />
              <Controles controles={e.controles} />
            </>
          )}
        </EtatsLecture>
      </section>
    </>
  );
}

// ─── Comparaison N / N-1 ───────────────────────────────────────────────────────────────────────
function TableComparaison({ lignes, n, n1 }) {
  const l = toArray(lignes);
  if (!l.length) return <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucun indicateur comparable.</p>;
  const signe = (v, t) => (v === null || v === undefined ? '—' : `${Number(v) > 0 ? '+ ' : Number(v) < 0 ? '− ' : ''}${cellule(Math.abs(Number(v)), t)}`);
  return (
    <div className="rh-conteneur-tableau">
      <table className="rh-tableau">
        <thead><tr><th>Indicateur</th><th style={{ textAlign: 'right' }}>{n}</th><th style={{ textAlign: 'right' }}>{n1}</th><th style={{ textAlign: 'right' }}>Écart</th><th style={{ textAlign: 'right' }}>Variation</th></tr></thead>
        <tbody>
          {l.map((x) => {
            const t = typeIndicateur(x.indicateur);
            const td = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
            return (
              <tr key={`${x.section}-${x.indicateur}`}>
                <td>{x.libelle}</td>
                <td style={td}>{cellule(x.n, t)}</td>
                <td style={td}>{cellule(x.n_1, t)}</td>
                <td style={td}>{signe(x.ecart, t)}</td>
                <td style={td}>{x.pourcentage === null || x.pourcentage === undefined ? '—' : `${x.pourcentage > 0 ? '+' : ''}${String(x.pourcentage).replace('.', ',').replace('-', '−')} %`}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function VueComparaison({ organisation, exercice }) {
  const api = useRapports();
  const l = useLectureSociale(() => api.comparaison(organisation.id, exercice), [api, organisation.id, exercice]);
  const c = l.donnees;
  return (
    <section className="rh-carte">
      <EtatsLecture lecture={l} Icone={History} estVide={false}>
        {c && (
          <>
            <TitreSection>Comparaison {c.exercice} / {c.exercice_precedent}</TitreSection>
            <Mentions mentions={c.mentions} />
            <TableComparaison lignes={c.lignes} n={c.exercice} n1={c.exercice_precedent} />
            <p className="rh-form-aide" style={{ margin: '8px 0 0' }}>Variation rapportée à l'exercice précédent ; « — » quand l'exercice précédent est nul.</p>
          </>
        )}
      </EtatsLecture>
    </section>
  );
}

// ─── Rapport annuel ────────────────────────────────────────────────────────────────────────────
/** Contenu d'un rapport annuel (aperçu provisoire ou document arrêté). */
function ContenuRapport({ r }) {
  const sections = r.sections || {};
  const codes = toArray(r.sommaire).map((s) => s.code).filter((c) => sections[c]);
  return (
    <>
      <Periode ex={r.exercice} />
      <Mentions mentions={r.mentions} />
      <h3 style={{ margin: '4px 0 8px', fontSize: 15, fontWeight: 600 }}>Comparaison avec l'exercice {r.exercice_precedent?.annee}</h3>
      <TableComparaison lignes={r.comparatif} n={r.exercice?.annee} n1={r.exercice_precedent?.annee} />
      <div style={{ marginTop: 16 }}>
        {codes.map((c, i) => (
          <details key={c} open={i === 0} style={{ borderTop: '1px solid var(--sg-bord)', padding: '10px 0' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 15 }}>
              {LIBELLES_ETATS[c]}{toArray(sections[c].controles).some((x) => !x.juste) ? ' — contrôle faux' : ''}
            </summary>
            <div style={{ marginTop: 10 }}><BlocRapport bloc={{ ...blocEtat(c, sections[c]), titre: null }} /></div>
          </details>
        ))}
      </div>
      <div style={{ borderTop: '1px solid var(--sg-bord)', paddingTop: 12 }}>
        <Controles controles={r.controles} avecSection titre="Contrôles de l'ensemble du rapport" />
      </div>
    </>
  );
}

function FenetreArreterRapport({ organisation, rapport, onFermer, onFait }) {
  const api = useRapports();
  const annee = rapport.exercice?.annee;
  return (
    <FenetreActe titre={`Arrêter le rapport annuel ${annee}`} Icone={Lock} libelleBouton="Arrêter le rapport" onFermer={onFermer}
      aide="Le rapport arrêté est figé : son contenu, ses empreintes et sa référence ne changent plus. Une correction passe par une réédition motivée, qui crée une nouvelle version ; l'ancienne reste consultable."
      onConfirmer={async (cle) => {
        const d = await api.arreterRapport(organisation.id, annee, cle);
        onFait(`Rapport ${annee} arrêté : ${d.reference}, version ${d.version}${d.controles_justes === false ? ' (avec des contrôles faux, à expliquer)' : ''}.`);
      }}>
      {rapport.controles_justes === false && (
        <BandeauInfo ton="danger" titre="Des contrôles sont faux">Les équations du rapport ne sont pas toutes justes : expliquez les écarts avant d'arrêter, ou arrêtez en connaissance de cause.</BandeauInfo>
      )}
      <p style={{ margin: '0 0 12px', fontSize: 14 }}>Exercice {annee}, du {frDate(rapport.exercice?.debut)} au {frDate(rapport.exercice?.fin)}.</p>
    </FenetreActe>
  );
}

function VueRapport({ organisation, exercice, droits, onArrete }) {
  const api = useRapports();
  const l = useLectureSociale(() => api.rapport(organisation.id, exercice), [api, organisation.id, exercice]);
  const [fenetre, setFenetre] = useState(false);
  const [info, setInfo] = useState('');
  const r = l.donnees;
  return (
    <section className="rh-carte">
      <EtatsLecture lecture={l} Icone={FileText} estVide={false}>
        {r && (
          <>
            <TitreSection action={droits.a('ds_rapport_arreter') && <Bouton variante="primaire" Icone={Lock} onClick={() => setFenetre(true)}>Arrêter le rapport</Bouton>}>
              Rapport annuel {r.exercice?.annee} <Badge ton="alerte" point>Provisoire</Badge>
            </TitreSection>
            {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
            <BoutonsExport exporter={(f) => api.exporterRapport(organisation.id, exercice, f)} libelle="Exporter le rapport" />
            <ContenuRapport r={r} />
          </>
        )}
      </EtatsLecture>
      {fenetre && r && <FenetreArreterRapport organisation={organisation} rapport={r} onFermer={() => setFenetre(false)} onFait={(m) => { setFenetre(false); setInfo(m); if (onArrete) onArrete(); }} />}
    </section>
  );
}

// ─── Documents arrêtés ─────────────────────────────────────────────────────────────────────────
const Empreinte = ({ libelle: lib, valeur }) => (valeur ? (
  <div style={{ fontSize: 13, margin: '2px 0' }}>
    <span style={{ color: 'var(--sg-texte-3)' }}>{lib} : </span>
    <code style={{ wordBreak: 'break-all' }}>{valeur}</code>
  </div>
) : null);

function DetailNominatif({ campagne, lignes }) {
  const l = toArray(lignes);
  if (!l.length) return <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucune inscription.</p>;
  const td = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  return (
    <div className="rh-conteneur-tableau">
      <table className="rh-tableau">
        <thead><tr><th>Salarié</th><th style={{ textAlign: 'right' }}>Attribuées</th><th style={{ textAlign: 'right' }}>Remises</th><th style={{ textAlign: 'right' }}>Prix</th>
          <th style={{ textAlign: 'right' }}>Aide</th><th style={{ textAlign: 'right' }}>Dû</th><th style={{ textAlign: 'right' }}>Reste dû</th><th>Retenues</th></tr></thead>
        <tbody>
          {l.map((x) => (
            <tr key={x.inscription_id || x.numero}>
              <td><span className="rh-cellule-principale">{x.salarie}</span><span className="rh-cellule-secondaire" style={{ display: 'block' }}>{x.matricule} · {x.numero}{x.remises ? ` · ${x.remises}` : ''}</span></td>
              <td style={td}>{cellule(x.attribue, 'nombre')}</td><td style={td}>{cellule(x.remis, 'nombre')}</td>
              <td style={td}>{francs(x.montant_brut)}</td><td style={td}>{francs(x.aide_imputee)}</td><td style={td}>{francs(x.montant_constitue)}</td>
              <td style={td}>{x.creance ? francs(x.creance.solde) : '—'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{x.paie ? `${x.paie.proposees} proposée(s), ${x.paie.bloquees} bloquée(s)` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {campagne && <p className="rh-form-aide" style={{ margin: '6px 0 0' }}>Document nominatif : diffusion restreinte aux personnes habilitées ; consultation tracée.</p>}
    </div>
  );
}

function FenetreReediter({ document: d, onFermer, onFait }) {
  const api = useRapports();
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre={`Réouvrir ${d.reference} (nouvelle version)`} Icone={History} libelleBouton="Rééditer" pret={motifValide(motif)} onFermer={onFermer}
      aide={`Le document est recalculé et arrêté à nouveau sous la même référence, en version ${d.version + 1}. La version ${d.version} passe « remplacée » et reste consultable avec ses empreintes.`}
      onConfirmer={async (cle) => { const r = await api.reediter(d.id, motif.trim(), cle); onFait(`Nouvelle version arrêtée : ${r.reference}, version ${r.version}.`); }}>
      <ChampMotif valeur={motif} onChange={setMotif} aide="5 caractères au moins ; le motif figure dans le document et dans l'historique." />
    </FenetreActe>
  );
}

function FenetreDocument({ id, droits, onFermer, onReedite }) {
  const api = useRapports();
  const l = useLectureSociale(() => api.arrete(id), [api, id]);
  const [reedition, setReedition] = useState(false);
  const a = l.donnees;
  const d = a?.document;
  const en = a?.entete || {};
  const donnees = a?.donnees || {};
  return (
    <Fenetre titre={d ? `${libelle(TYPES_DOCUMENT, d.type_document)} ${d.reference} — version ${d.version}` : 'Document arrêté'} Icone={FileLock2} onFermer={onFermer} large>
      <EtatsLecture lecture={l} Icone={FileLock2} estVide={false}>
        {a && d && (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
              <Etat table={STATUTS_DOCUMENT} code={d.statut} />
              {a.integrite?.conforme
                ? <Badge ton="succes" point>Intégrité conforme</Badge>
                : <Badge ton="danger" point>Intégrité NON conforme</Badge>}
              {d.demonstration && <Badge ton="alerte">[DÉMO]</Badge>}
            </div>
            <p style={{ margin: '0 0 6px', fontSize: 14 }}>
              Arrêté le {dateHeureLongue(en.arrete_le || d.genere_le)}{en.arrete_par?.fonction ? ` par la fonction ${en.arrete_par.fonction}` : ''}{en.arrete_par?.par_interim ? ' (par intérim)' : ''}.
              {d.exercice ? ` Exercice ${d.exercice}.` : ''}
            </p>
            {en.remplace && <p style={{ margin: '0 0 6px', fontSize: 14 }}>Remplace la version {en.remplace.version}.</p>}
            {d.motif_reedition && <p style={{ margin: '0 0 6px', fontSize: 14 }}>Motif de la réédition : « {d.motif_reedition} »</p>}
            <Empreinte libelle="Empreinte SHA-256 des données" valeur={d.empreinte_donnees} />
            <Empreinte libelle="Empreinte SHA-256 du document" valeur={d.empreinte_fichier} />
            {a.integrite && !a.integrite.conforme && <BandeauInfo ton="danger" titre="Empreintes non conformes">Le contenu stocké ne correspond plus aux empreintes enregistrées : signalez-le à la commission de contrôle.</BandeauInfo>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', margin: '10px 0 0' }}>
              <BoutonsExport exporter={(f) => api.exporterArrete(d.id, f)} libelle="Exporter ce document" />
              {d.statut === 'ARRETE' && droits.a('ds_rapport_arreter') && <Bouton Icone={History} onClick={() => setReedition(true)}>Réouvrir (nouvelle version)</Bouton>}
            </div>
            <div style={{ borderTop: '1px solid var(--sg-bord)', paddingTop: 12 }}>
              {d.type_document === 'RAPPORT_ANNUEL' && <ContenuRapport r={donnees} />}
              {d.type_document === 'ETAT_CAMPAGNE' && donnees.agrege && (
                <>
                  <Mentions mentions={donnees.agrege.mentions || en.mentions} />
                  <BlocRapport bloc={blocEtatCampagne(donnees.agrege.campagne || {})} />
                  <Controles controles={donnees.agrege.controles} />
                  <h4 style={{ margin: '8px 0 6px', fontSize: 14, fontWeight: 600 }}>Détail nominatif</h4>
                  {a.detail_nominatif_masque
                    ? <p style={{ margin: 0, fontSize: 14 }}>Détail nominatif masqué : il est réservé aux personnes à qui la clé « détail nominatif » (ds_export_nominatif) est attribuée.</p>
                    : <DetailNominatif campagne lignes={donnees.detail_nominatif?.salaries} />}
                </>
              )}
            </div>
          </>
        )}
      </EtatsLecture>
      {reedition && d && <FenetreReediter document={d} onFermer={() => setReedition(false)} onFait={(m) => { setReedition(false); l.recharger(); if (onReedite) onReedite(m); }} />}
    </Fenetre>
  );
}

function VueArretes({ organisation, droits, version }) {
  const api = useRapports();
  const [type, setType] = useState('');
  const [page, setPage] = useState(1);
  const [ouvert, setOuvert] = useState(null);
  const [info, setInfo] = useState('');
  const [v, setV] = useState(0);
  const l = useLectureSociale(() => api.arretes(organisation.id, { type, page }), [api, organisation.id, type, page, version, v]);
  const docs = toArray(l.donnees);
  return (
    <>
      <Pastilles options={[['', 'Tous'], ...Object.entries(TYPES_DOCUMENT)]} valeur={type} onChange={(t) => { setType(t); setPage(1); }} libelleGroupe="Type de document" />
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      <section className="rh-carte" style={{ padding: 0 }}>
        <EtatsLecture lecture={l} Icone={FileLock2} estVide={!docs.length} titreVide="Aucun document arrêté"
          vide="Un rapport annuel ou un état de campagne arrêté apparaît ici avec toutes ses versions et ses empreintes.">
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Document</th><th>Version</th><th>Exercice</th><th>Statut</th><th>Arrêté le</th><th>Empreinte des données</th><th aria-label="Consulter" /></tr></thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id}>
                    <td><span className="rh-cellule-principale">{d.reference}</span><span className="rh-cellule-secondaire" style={{ display: 'block' }}>{libelle(TYPES_DOCUMENT, d.type_document)}</span></td>
                    <td>v{d.version}{d.motif_reedition ? <span className="rh-cellule-secondaire" style={{ display: 'block', maxWidth: 200 }}>{d.motif_reedition}</span> : null}</td>
                    <td>{d.exercice || '—'}</td>
                    <td><Etat table={STATUTS_DOCUMENT} code={d.statut} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{dateHeureLongue(d.genere_le)}</td>
                    <td><code title={d.empreinte_donnees} style={{ fontSize: 12 }}>{String(d.empreinte_donnees || '').slice(0, 16)}…</code></td>
                    <td style={{ textAlign: 'right' }}><Bouton petit onClick={() => setOuvert(d.id)}>Consulter</Bouton></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationDs donnees={l.donnees} onPage={setPage} />
        </EtatsLecture>
      </section>
      {ouvert && <FenetreDocument id={ouvert} droits={droits} onFermer={() => setOuvert(null)} onReedite={(m) => { setInfo(m); setOuvert(null); setV((x) => x + 1); }} />}
    </>
  );
}

// ─── État de campagne ──────────────────────────────────────────────────────────────────────────
function NominatifCampagne({ campagneId }) {
  const api = useRapports();
  const [page, setPage] = useState(1);
  const l = useLectureSociale(() => api.detailCampagne(campagneId, { page, taille: 25 }), [api, campagneId, page]);
  if (l.erreur && l.erreur.statut === 403) {
    return (
      <BandeauInfo ton="danger" titre="Détail nominatif non accessible">
        Le détail nominatif (« à qui, et où est passé chaque montant ») est réservé aux personnes à qui la clé « détail nominatif » (ds_export_nominatif) a été attribuée.
        Tant qu'elle ne vous est pas attribuée, seul l'état agrégé, sans nom, est consultable.
        <span style={{ display: 'block', marginTop: 4, color: 'var(--sg-texte-2)' }}>Réponse du serveur : {l.erreur.message}</span>
      </BandeauInfo>
    );
  }
  const d = l.donnees;
  return (
    <EtatsLecture lecture={l} Icone={Users} estVide={false}>
      {d && (
        <>
          <Mentions mentions={d.mentions} />
          <BoutonsExport exporter={(f) => api.exporterDetailCampagne(campagneId, f)} libelle="Exporter le détail nominatif" />
          <DetailNominatif campagne lignes={d.salaries?.elements || d.salaries} />
          <PaginationDs donnees={d.salaries} onPage={setPage} />
        </>
      )}
    </EtatsLecture>
  );
}

function FenetreArreterCampagne({ campagne, etat, onFermer, onFait }) {
  const api = useRapports();
  const justes = toArray(etat.controles).every((c) => c.juste);
  return (
    <FenetreActe titre={`Arrêter l'état de la campagne ${campagne.numero}`} Icone={Lock} libelleBouton="Arrêter l'état" onFermer={onFermer}
      aide="L'état arrêté fige les unités, les achats, les montants des salariés et le détail nominatif (consultable par les seules personnes habilitées). Une correction passe par une réédition motivée."
      onConfirmer={async (cle) => { const d = await api.arreterCampagne(campagne.id, cle); onFait(`État de campagne arrêté : ${d.reference}, version ${d.version}.`); }}>
      {!justes && <BandeauInfo ton="danger" titre="Des contrôles sont faux">Expliquez les écarts avant d'arrêter l'état.</BandeauInfo>}
    </FenetreActe>
  );
}

function VueCampagne({ organisation, droits, onArrete }) {
  const ca = useCampagnes();
  const api = useRapports();
  const liste = useLectureSociale(() => ca.campagnes(organisation.id, { page: 1, taille: 100 }), [ca, organisation.id]);
  const campagnes = toArray(liste.donnees);
  const [id, setId] = useState('');
  const [nominatif, setNominatif] = useState(false);
  const [fenetre, setFenetre] = useState(false);
  const [info, setInfo] = useState('');
  const choisie = campagnes.find((c) => c.id === id) || null;
  const etat = useLectureSociale(() => (id ? api.etatCampagne(id) : Promise.resolve(null)), [api, id]);
  const e = etat.donnees;
  return (
    <section className="rh-carte">
      <EtatsLecture lecture={liste} Icone={FileText} estVide={!campagnes.length} titreVide="Aucune campagne" vide="Aucune campagne dans cette organisation.">
        <Champ libelle="Campagne">
          <select className="rh-champ-saisie" value={id} onChange={(x) => { setId(x.target.value); setNominatif(false); setInfo(''); }} style={{ maxWidth: 480 }}>
            <option value="">Choisir une campagne…</option>
            {campagnes.map((c) => <option key={c.id} value={c.id}>{c.numero} — {c.libelle}{c.simulation ? ' (simulation)' : ''}</option>)}
          </select>
        </Champ>
      </EtatsLecture>
      {id && (
        <EtatsLecture lecture={etat} Icone={FileText} estVide={false}>
          {e && choisie && (
            <>
              <TitreSection action={droits.a('ds_rapport_arreter') && <Bouton variante="primaire" Icone={Lock} onClick={() => setFenetre(true)}>Arrêter l'état</Bouton>}>
                État de la campagne {choisie.numero}
              </TitreSection>
              {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
              <Mentions mentions={e.mentions} />
              <BoutonsExport exporter={(f) => api.exporterEtatCampagne(id, f)} libelle="Exporter l'état (sans nom)" />
              <BlocRapport bloc={blocEtatCampagne(e.campagne || {})} />
              <Controles controles={e.controles} />
              <div style={{ borderTop: '1px solid var(--sg-bord)', paddingTop: 12 }}>
                <TitreSection action={!nominatif && <Bouton Icone={Users} onClick={() => setNominatif(true)}>Afficher le détail nominatif</Bouton>}>Détail nominatif</TitreSection>
                {!nominatif && <p className="rh-form-aide" style={{ margin: 0 }}>Réservé aux personnes habilitées ; chaque consultation est tracée.</p>}
                {nominatif && <NominatifCampagne campagneId={id} />}
              </div>
            </>
          )}
        </EtatsLecture>
      )}
      {fenetre && choisie && e && <FenetreArreterCampagne campagne={choisie} etat={e} onFermer={() => setFenetre(false)} onFait={(m) => { setFenetre(false); setInfo(m); if (onArrete) onArrete(); }} />}
    </section>
  );
}

// ─── Onglet ────────────────────────────────────────────────────────────────────────────────────
const VUES = [['etats', 'États'], ['comparaison', 'Comparaison N / N-1'], ['rapport', 'Rapport annuel'], ['arretes', 'Documents arrêtés'], ['campagne', 'État de campagne']];

export default function RapportsSection({ organisation, droits }) {
  const api = useRapports();
  const ds = useDs();
  const ex = useLectureSociale(() => api.exercices(organisation.id), [api, organisation.id]);
  const disp = useLectureSociale(() => ds.dispositifs(organisation.id, { page: 1, taille: 100 }), [ds, organisation.id]);
  const [vue, setVue] = useState('etats');
  const [exercice, setExercice] = useState('');
  const [dispositif, setDispositif] = useState('');
  const [version, setVersion] = useState(0);
  useEffect(() => { if (ex.donnees && !exercice) setExercice(String(ex.donnees.courant)); }, [ex.donnees, exercice]);
  const exercices = toArray(ex.donnees?.exercices);
  const dispositifs = toArray(disp.donnees).filter((d) => ['PRET', 'AIDE', 'CAMPAGNE'].includes(d.type));
  const libelleDispositif = (dispositifs.find((d) => d.id === dispositif) || {}).libelle || '';
  if (ex.erreur) {
    return <section className="rh-carte"><EtatsLecture lecture={ex} estVide={false}>{null}</EtatsLecture></section>;
  }
  if (!ex.donnees || !exercice) return <section className="rh-carte"><Squelette largeur="40%" /><Squelette largeur="60%" style={{ marginTop: 8 }} /></section>;
  return (
    <>
      <div className="rh-outils" style={{ alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, color: 'var(--sg-texte-2)' }}>
          <span style={{ display: 'block', marginBottom: 4 }}>Exercice</span>
          <select className="rh-champ-saisie" value={exercice} onChange={(x) => setExercice(x.target.value)} style={{ minWidth: 120 }}>
            {exercices.map((x) => <option key={x.annee} value={String(x.annee)}>{x.annee}{x.en_cours ? ' (en cours)' : ''}</option>)}
          </select>
        </label>
        {vue === 'etats' && (
          <label style={{ fontSize: 13, color: 'var(--sg-texte-2)' }}>
            <span style={{ display: 'block', marginBottom: 4 }}>Dispositif</span>
            <select className="rh-champ-saisie" value={dispositif} onChange={(x) => setDispositif(x.target.value)} style={{ minWidth: 200, maxWidth: '100%' }}>
              <option value="">Tous les dispositifs</option>
              {dispositifs.map((d) => <option key={d.id} value={d.id}>{d.libelle}</option>)}
            </select>
          </label>
        )}
      </div>
      <Pastilles options={VUES} valeur={vue} onChange={setVue} libelleGroupe="Rapports" />
      {!droits.a('ds_rapport_arreter') && ['rapport', 'arretes', 'campagne'].includes(vue) && (
        <p className="rh-form-aide" style={{ margin: '0 0 8px', display: 'flex', gap: 6, alignItems: 'center' }}><ShieldCheck size={14} aria-hidden="true" />L'arrêt et la réédition des documents reviennent à la fonction qui détient la clé d'arrêt (Président).</p>
      )}
      {vue === 'etats' && <VueEtats organisation={organisation} exercice={exercice} dispositif={dispositif} libelleDispositif={libelleDispositif} />}
      {vue === 'comparaison' && <VueComparaison organisation={organisation} exercice={exercice} />}
      {vue === 'rapport' && <VueRapport organisation={organisation} exercice={exercice} droits={droits} onArrete={() => setVersion((v) => v + 1)} />}
      {vue === 'arretes' && <VueArretes organisation={organisation} droits={droits} version={version} />}
      {vue === 'campagne' && <VueCampagne organisation={organisation} droits={droits} onArrete={() => setVersion((v) => v + 1)} />}
    </>
  );
}

